import axios, {
  type AxiosError,
  type AxiosHeaders,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios";
import store from "./store";
import { User } from "./modules/security/models/user-model";

declare const AXIOS_BASE: string;
declare const AXIOS_HEADERS: string;

/**
 * Subset of AxiosRequestConfig
 */
export type RequestConfig<TData = unknown> = {
  baseURL?: string;
  url?: string;
  method: "get" | "put" | "patch" | "post" | "delete";
  params?: unknown;
  data?: TData;
  responseType?:
    | "arraybuffer"
    | "blob"
    | "document"
    | "json"
    | "text"
    | "stream";
  signal?: AbortSignal;
  controller?: AbortController;
  headers?: AxiosRequestConfig["headers"];
  onDownloadProgress?: (progressEvent: ProgressEvent) => void;
};
/**
 * Subset of AxiosResponse
 */
export type ResponseConfig<TData = unknown> = {
  data: TData;
  status: number;
  statusText: string;
  headers?: AxiosResponse["headers"];
};

export const axiosInstance = axios.create({
  baseURL: typeof AXIOS_BASE !== "undefined" ? AXIOS_BASE : undefined,
  headers:
    typeof AXIOS_HEADERS !== "undefined"
      ? (JSON.parse(AXIOS_HEADERS) as AxiosHeaders)
      : undefined,
});

export const axiosClient = async <
  TData,
  TError = unknown,
  TVariables = unknown,
>(
  config: RequestConfig<TVariables>,
): Promise<ResponseConfig<TData>> => {
  config.controller = new AbortController();
  config.signal = config.controller.signal;
  const user: User = store.getters["security/getUser"]();
  if (user && user.isEmployee() &&  config.url) {
    config.url = config.url.replace(/(api\/)/, "$1personale/");
  }

  const promise = axios
    .request<TData, ResponseConfig<TData>>(config as AxiosRequestConfig)
    .catch((e: AxiosError<TError>) => {
      throw e;
    });

  return promise;
};

export default axiosClient;
